import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Spin } from 'antd';

import { useUserStore } from '../../../../store/useUser';

import { getAttendanceSummaryByMeetId, registerMeetAttendance } from '../../../../service/attendance';
import { getClubUser } from '../../../../service/clubUser';

import DefaultContainer from '../../../../components/DefaultContainer/DefaultContainer';

import { main_color, main_color_50 } from '../../../../styles/_variables';
import styles from './RegisterAttendancesList.module.scss';
import Icon from '../../../../components/Icon/Icon';
import { getMeetById } from '../../../../service/meet';
import { formatDateLocale } from '../../../../utils/formatDateLocale';
import { getBook } from '../../../../service/book';
import { getClubMember } from '../../../../service/clubs';
import RegisterAttendanceItem from '../RegisterAttendanceItem/RegisterAttendanceItem';
import { isDateWithinSevenDays } from '../../../../utils/date';
import { AttendanceRecord } from '../../../../models/attendance';
import Button from '../../../../components/Button/Button';
import { createErrorNotification, createSuccessNotification } from '../../../../service/notification';

const RegisterAttendancesList: React.FC = () => {
  const navigate = useNavigate()
  const { id, name: clubName, meetId : meet_id } = useParams();

  const clubId = Number(id);
  const meetId = Number(meet_id);
  
  const userId = useUserStore(state => state.id);
  const profile = useUserStore(state => state.profile);
  const [attendances, setAttendances] = useState<AttendanceRecord[]>([]);

  const { data: attendanceSummary, isFetching: isFetchingSummary } = useQuery(
    ['summary', meetId],
    () => getAttendanceSummaryByMeetId(meetId, clubId),
    { refetchOnMount: 'always', staleTime: 0, refetchOnWindowFocus: false },
  );

  const { data: meet, isFetching: isFetchingMeet } = useQuery(
    ['meet', meetId],
    () => getMeetById(meetId),
    { refetchOnMount: 'always', staleTime: 0, refetchOnWindowFocus: false },
  );

  const { data: book, isFetching: isFetchingBook } = useQuery(
    ['book', meet],
    () => {
      if (meet?.bookId) return getBook(meet?.bookId);
      return undefined;
    },
    { refetchOnMount: 'always', staleTime: 0, refetchOnWindowFocus: false },
  );

  const { data:clubUser, isFetching: isFetchingClubUser } = useQuery(
    ['clubUser', clubId],
    () => getClubUser(userId, clubId),
    {refetchOnMount: 'always', staleTime: 0, refetchOnWindowFocus: false},
  );

  const { data:clubMembers, isFetching: isFetchingClubMembers } = useQuery(
    ['clubMembers', clubId],
    () => getClubMember(clubId, 1),
    {refetchOnMount: 'always', staleTime: 0, refetchOnWindowFocus: false},
  );

  useEffect(() => {
    const newAttendances = clubMembers?.map(member => ({
      userId: member.userId,
      wasInAttendance: 0 as 0 | 1
    }));
  
    if (newAttendances) {
      setAttendances(newAttendances);
    }
  }, [clubMembers]);

  
  const updateAttendances = (userId: number, wasInAttendance: boolean) => {
    setAttendances(prevAttendances => {
      const updatedAttendances : AttendanceRecord[] = prevAttendances.map(attendance => {
        if (attendance.userId === userId) {
          return {
            ...attendance,
            wasInAttendance: wasInAttendance ? 1 : 0
          };
        }
        return attendance;
      });
      return updatedAttendances;
    });
  };

  const registerAttendances = async () => {
    const {error, success} = await registerMeetAttendance(attendances, clubId, meetId);
    if(error) return createErrorNotification(error);
    createSuccessNotification('Lista de presença registrada com sucesso!');
  
    navigate(`/club/${clubId}/${clubName}/attendances/meet/${meetId}`)
  }

  const isAdminOrMediator = Boolean(clubUser?.isCreator === 1 || clubUser?.isMediator === 1);

  const isFetching = isFetchingClubUser || isFetchingSummary || isFetchingClubMembers || isFetchingMeet || isFetchingBook;

  if (attendanceSummary && (!isDateWithinSevenDays(attendanceSummary?.dateMeet))) return <DefaultContainer>Prazo de registrar presenças expirou!</DefaultContainer>;
  if (attendanceSummary?.totalMembers > 0) navigate(`/club/${clubId}/${clubName}/attendances/meet/${meetId}`);
  if (!userId) return <DefaultContainer>Você precisa fazer login</DefaultContainer>;
  if((!isAdminOrMediator) || (profile === "Clubista")) return <DefaultContainer>Você não tem permissão pra vê essa página</DefaultContainer>;

  return (
      <div className={styles.header}>
        <div className={styles.titleHeader}>
          <Icon className={styles.icon} icon='calendar' color={main_color} />
          <span className={styles.name}>{formatDateLocale(meet?.dateMeet)}</span>
        </div>
        <div 
          className={styles.titleHeader}
          onClick={() => navigate(`/book/local/${meet?.bookId}`)}
        >
          <Icon className={styles.icon} icon='bookcase' color={main_color} />
          <span className={styles.name}>{book?.name} ({book?.author})</span>
        </div>
        <Spin spinning={isFetching}>
          {clubMembers?.map(member => 
              <RegisterAttendanceItem member={member} onAttendanceChange={updateAttendances} />
          )}
        </Spin>
        <Button 
            style={styles.button}
            onClick={registerAttendances}>
            Registrar presenças
          </Button>
      </div>
  )
}

export default RegisterAttendancesList;